import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import {
  updateWorkoutInfoColor,
  updateNonDateBasedWorkoutInfo,
} from '../ducks/calendarActions';

// Colors for the workout color selection
export const colorOptions = [
  { key: 'red', color: '#D16D6A' },
  { key: 'orange', color: '#ECB576' },
  { key: 'yellow', color: '#F9DA78' },
  { key: 'green', color: '#9DC384' },
  { key: 'blue-ish', color: '#80A4AE' },
  { key: 'blue-ish-ish', color: '#7CA6D7' },
  { key: 'purple', color: '#8B7DBE' },
  { key: 'mauve?', color: '#B87E9E' },
  { key: 'grey', color: '#777777' },
  { key: 'no-color-option', color: 'blank' },
];

/**
 * Shared workout-color update logic, used by both the CalendarDropdown color
 * submenu and the day-header WorkoutColorCircle. Reads the contextual selectors
 * (current user, selected athlete/calendar/program) from Redux; the caller
 * supplies the resolved day context so each call site keeps its existing
 * date/day resolution.
 */
const useWorkoutColorUpdate = ({
  programType,
  workoutDetails,
  effectiveDate,
  effectiveDayNumber,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const selectedCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );

  // Optimistic override so the color circle repaints immediately on click instead
  // of waiting for the update thunk's PUT request to round-trip. Cleared once the
  // thunk's promise settles (it never rejects - it catches internally), at which
  // point Redux is already in its final state, so falling back to workoutDetails is
  // safe whether the update succeeded or failed.
  const [optimisticColor, setOptimisticColor] = useState(null);

  // Tags each handleUpdateColor call so an earlier, slower-resolving request can't
  // clear the optimistic color a later pick set - otherwise two rapid picks can
  // briefly flash back to the stale first color when the first request settles
  // after the second one was already dispatched.
  const latestUpdateRef = useRef(0);

  // Guards day view, where a single hook instance is reused across days as the
  // user navigates (workingDate = date || selectedDate) - drop any pending
  // optimistic pick from the previous day so it can't bleed into the next one.
  useEffect(() => {
    setOptimisticColor(null);
  }, [effectiveDate, effectiveDayNumber]);

  const handleUpdateColor = (color) => {
    const resolvedColor = color === 'blank' ? '' : color;
    setOptimisticColor(resolvedColor);
    latestUpdateRef.current += 1;
    const updateToken = latestUpdateRef.current;

    let submissionObject;
    let pendingUpdate;
    if (programType === 'free-form-program') {
      submissionObject = {
        color: resolvedColor,
        title: workoutDetails?.title || '',
      };
      const savedWorkoutId = selectedReduxNonDateBasedProgram.id;
      pendingUpdate = dispatch(updateNonDateBasedWorkoutInfo(
        currentUser.accountCode,
        savedWorkoutId,
        effectiveDayNumber,
        submissionObject,
        'color',
      ));
    } else {
      submissionObject = {
        color: resolvedColor,
        date: moment(effectiveDate).format('YYYY-MM-DD'),
      };
      if (workoutDetails?.title) {
        submissionObject.title = workoutDetails.title;
      }
      if (Object.keys(selectedReduxAthlete).length) {
        submissionObject.userId = selectedReduxAthlete.id;
      } else {
        submissionObject.calendarId = selectedCalendar.id;
      }

      pendingUpdate = dispatch(updateWorkoutInfoColor(
        currentUser.accountCode,
        submissionObject,
      ));
    }

    pendingUpdate.then(() => {
      if (latestUpdateRef.current === updateToken) {
        setOptimisticColor(null);
      }
    });
  };

  const displayColor = optimisticColor !== null ? optimisticColor : workoutDetails?.color;

  return { colorOptions, handleUpdateColor, displayColor };
};

export default useWorkoutColorUpdate;
